import * as z from "zod";

/** IMS subscription pricing tiers based on monthly order volume */
export const IMS_PRICING_TIERS = [
  { orderThreshold: 0, monthlyInCents: 34_900 }, // 0-499 orders: $349/mo
  { orderThreshold: 500, monthlyInCents: 49_900 }, // 500-999 orders: $499/mo
  { orderThreshold: 1000, monthlyInCents: 59_900 }, // 1k-2.5k orders: $599/mo
  { orderThreshold: 2500, monthlyInCents: 79_900 }, // 2.5k-5k orders: $799/mo
  { orderThreshold: 5000, monthlyInCents: 149_900 }, // 5k-10k orders: $1,499/mo
  { orderThreshold: 10_000, monthlyInCents: 299_900 }, // 10k+ orders: $2,999/mo
] as const;

/** AI-Forecasting add-on pricing tiers based on monthly order volume */
export const FORECASTING_PRICING_TIERS = [
  { orderThreshold: 0, monthlyInCents: 39_900 }, // 0-499 orders: $399/mo
  { orderThreshold: 500, monthlyInCents: 49_900 }, // 500-999 orders: $499/mo
  { orderThreshold: 1000, monthlyInCents: 69_900 }, // 1k-2.5k orders: $699/mo
  { orderThreshold: 2500, monthlyInCents: 99_900 }, // 2.5k-5k orders: $999/mo
  { orderThreshold: 5000, monthlyInCents: 199_900 }, // 5k-10k orders: $1,999/mo
  { orderThreshold: 10_000, monthlyInCents: 299_900 }, // 10k+ orders: $2,999/mo
] as const;

/** IMS Bundle (IMS + Forecasting) pricing tiers based on monthly order volume */
export const BUNDLE_PRICING_TIERS = [
  { orderThreshold: 0, monthlyInCents: 39_900 }, // 0-499 orders: $399/mo
  { orderThreshold: 500, monthlyInCents: 59_900 }, // 500-999 orders: $599/mo
  { orderThreshold: 1000, monthlyInCents: 79_900 }, // 1k-2.5k orders: $799/mo
  { orderThreshold: 2500, monthlyInCents: 129_900 }, // 2.5k-5k orders: $1,299/mo
  { orderThreshold: 5000, monthlyInCents: 249_900 }, // 5k-10k orders: $2,499/mo
  { orderThreshold: 10_000, monthlyInCents: 399_900 }, // 10k+ orders: $3,999/mo
] as const;

/** Annual billing discount percentage */
export const ANNUAL_BILLING_DISCOUNT = 0.1; // 10% discount

const PricingTierSchema_ = z.object({
  orderThreshold: z.number(),
  monthlyInCents: z.number(),
  orders: z.string(),
  monthly: z.string(),
});
export const PricingTierSchema: z.ZodType<PricingTier> = PricingTierSchema_;
export interface PricingTier extends z.infer<typeof PricingTierSchema_> {}

export const getImsPricingInfo = {
  name: "getImsPricingInfo" as const,
  input: z.object({}),
  output: z.object({
    /** Estimated monthly order volume, or null if no data available */
    monthlyOrders: z.number().nullable(),
    /** Whether order data is available */
    orderDataAvailable: z.boolean(),
    /** Index of the matching pricing tier, or null if no data */
    matchingTierIndex: z.number().nullable(),
    /** Whether self-enrollment is blocked */
    selfEnrollmentBlocked: z.boolean(),
    /** Reason why self-enrollment is blocked, if applicable */
    selfEnrollmentBlockedReason: z.enum(["no_order_data"]).optional(),
    /** IMS-only pricing tiers */
    imsPricingTiers: z.array(PricingTierSchema),
    /** AI-Forecasting add-on pricing tiers */
    forecastingPricingTiers: z.array(PricingTierSchema),
    /** Bundle (IMS + Forecasting) pricing tiers */
    bundlePricingTiers: z.array(PricingTierSchema),
    /** Annual billing discount percentage */
    annualBillingDiscount: z.number(),
  }),
};
